import { Link } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import { Breadcrumbs } from '@/components/Breadcrumbs';

export const AboutUs = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="about-page">
      <SEOHead
        title="About Us"
        description="Learn about Jyotirlinga.info — a comprehensive pilgrimage guide to the 12 sacred Jyotirlinga temples of Lord Shiva across India. Our mission, values, and story."
        keywords="about jyotirlinga.info, pilgrimage guide, shiva temples india, mission"
        canonical="https://jyotirlinga.info/about"
      />
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl">
          <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'About Us' }]} />
          <h1 className="font-cormorant text-4xl sm:text-5xl font-bold tracking-tighter text-temple-slate mt-8 mb-8">
            About 12 Jyotirlingas
          </h1>
          <div className="font-outfit text-gray-700 space-y-6 leading-relaxed">
            <p className="text-lg">
              <strong>Jyotirlinga.info</strong> is a devotional resource dedicated to the twelve sacred abodes of Lord Shiva — the Dwadasa Jyotirlingas — spread across the length and breadth of India from the shores of Gujarat to the peaks of the Himalayas, from the temples of Tamil Nadu to the holy city of Varanasi.
            </p>

            <h2 className="font-cormorant text-2xl font-bold text-temple-slate mt-8">Our Mission</h2>
            <p>To make the ancient pilgrimage tradition of visiting the 12 Jyotirlingas accessible to every devotee — providing accurate information on temple history, Vedic significance, darshan timings, travel logistics, and spiritual practices in one comprehensive guide.</p>

            <h2 className="font-cormorant text-2xl font-bold text-temple-slate mt-8">What Is a Jyotirlinga?</h2>
            <p>A Jyotirlinga (from Sanskrit <em>jyoti</em> = radiance, <em>linga</em> = the sign of Shiva) is a devotional representation of Lord Shiva. According to the Shiva Purana, Lord Shiva manifested himself as an infinite column of light at twelve sacred sites across India. These twelve locations are considered the most powerful and holiest shrines of Shiva.</p>
            <p>The 12 Jyotirlingas are:</p>
            <ol className="list-decimal pl-6 space-y-1">
              <li><Link to="/temple/somnath" className="text-saffron-600 hover:underline">Somnath</Link> in Gujarat</li>
              <li><Link to="/temple/mallikarjuna" className="text-saffron-600 hover:underline">Mallikarjuna</Link> in Srisailam, Andhra Pradesh</li>
              <li><Link to="/temple/mahakaleshwar" className="text-saffron-600 hover:underline">Mahakaleshwar</Link> in Ujjain, Madhya Pradesh</li>
              <li><Link to="/temple/omkareshwar" className="text-saffron-600 hover:underline">Omkareshwar</Link> in Madhya Pradesh</li>
              <li><Link to="/temple/kedarnath" className="text-saffron-600 hover:underline">Kedarnath</Link> in Uttarakhand</li>
              <li><Link to="/temple/bhimashankar" className="text-saffron-600 hover:underline">Bhimashankar</Link> in Maharashtra</li>
              <li><Link to="/temple/kashi-vishwanath" className="text-saffron-600 hover:underline">Kashi Vishwanath</Link> in Varanasi, Uttar Pradesh</li>
              <li><Link to="/temple/trimbakeshwar" className="text-saffron-600 hover:underline">Trimbakeshwar</Link> in Nashik, Maharashtra</li>
              <li><Link to="/temple/vaidyanath" className="text-saffron-600 hover:underline">Vaidyanath</Link> in Deoghar, Jharkhand</li>
              <li><Link to="/temple/nageshwar" className="text-saffron-600 hover:underline">Nageshwar</Link> in Dwarka, Gujarat</li>
              <li><Link to="/temple/rameshwaram" className="text-saffron-600 hover:underline">Rameshwaram</Link> in Tamil Nadu</li>
              <li><Link to="/temple/grishneshwar" className="text-saffron-600 hover:underline">Grishneshwar</Link> in Aurangabad, Maharashtra</li>
            </ol>

            <h2 className="font-cormorant text-2xl font-bold text-temple-slate mt-8">What We Offer</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Detailed history and Puranic legend of each Jyotirlinga</li>
              <li>Kaal Sarp Dosh & Vedic astrological significance</li>
              <li>Darshan timings, aarti schedules, and special rituals</li>
              <li>Complete travel information — nearest airport, railway, road</li>
              <li>Accommodation guide with trusted booking partners</li>
              <li>Food, prasad, and vegetarian restaurant recommendations</li>
              <li>Nearby attractions and additional pilgrimage sites</li>
              <li>Interactive India map showing all 12 Jyotirlinga locations</li>
            </ul>

            <h2 className="font-cormorant text-2xl font-bold text-temple-slate mt-8">Our Values</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Authenticity:</strong> Information sourced from Puranic texts and verified pilgrimage sources</li>
              <li><strong>Accessibility:</strong> Free content available to every devotee, everywhere</li>
              <li><strong>Respect:</strong> Content presented with reverence for tradition and diverse practices</li>
              <li><strong>Utility:</strong> Practical guidance for real pilgrimages, not just theory</li>
            </ul>

            <p className="mt-8">May your journey to the sacred Jyotirlingas be blessed with peace, wisdom, and spiritual awakening. 🕉️</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
